/* global YT */

import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { functions } from "../../../../config/firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import AudioRecorder from "./AudioRecorder";
import AudioPlayer from "./AudioPlayer";
import { FaTrash, FaEllipsisH } from "react-icons/fa";
import AddTextModal from "./AddTextModal";
import MultipleChoiceQuestion from "./MultipleChoiceQuestion";
import MultipleCheckboxQuestion from "./MultipleCheckboxQuestion";
import SentenceOrdering from "./SentenceOrdering";
import FillInTheBlanks from "./FillInTheBlanks";
import SpellTheWord from "./SpellTheWord";
import Pairing from "./Pairing";
import "./ModuleDetailPage.css";
import { v4 as uuidv4 } from "uuid";
import {
  handleQuestionChange,
  createNewMaterial,
  createQuestionMaterial,
  isCountableQuestion,
} from "./helpers/materialHelpers";
import { categorizeMaterial } from "./AICategorization";
import TheGuessinator from "./games/TheGuessinator";
import Flashcards from "./games/Flashcards";
import OpenTextQuestion from "./OpenTextQuestion";
import AddContentModal from "./AddContentModal";
import TipButton from "../../../TipButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faCog,
  faMagicWandSparkles,
  faImage,
  faFont,
  faQuestion,
  faEye,
  faArrowLeft,
  faRetweet,
} from "@fortawesome/free-solid-svg-icons";
import ModuleSettings from "./ModuleSettings";
import { generatePrompt, processAIResponse } from "./AIGenerateContent";
import GenerateContentModal from "./GenerateContentModal";
import { UserContext } from "../../../../App";
import { useTrainingTips } from "../../../../hooks/useTrainingTips";
import {
  useTrainingButton,
  useTrainingSection,
} from "../../../../hooks/useTrainingElement";
import { useUnsavedChanges } from "../../../../contexts/UnsavedChangesContext";

const fetchModuleData = httpsCallable(functions, "fetchModuleData");
const updateModuleMaterials = httpsCallable(functions, "updateModuleMaterials");
const addMaterial = httpsCallable(functions, "addMaterial");
const getCourses = httpsCallable(functions, "getCourses");

const callAIService = async (prompt) => {
  try {
    const openAIProxy = httpsCallable(functions, "openAIProxy");
    const response = await openAIProxy({
      requestType: "chat",
      params: {
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content:
              "You are a language learning content creator. You must respond with ONLY valid JSON, no markdown formatting, no explanations.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        temperature: 0.7,
      },
    });

    let content = response.data.choices[0].message.content;

    // Clean the response if it contains markdown
    if (content.includes("```")) {
      content = content.replace(/```json\n|\```\n|```/g, "");
    }

    return content.trim();
  } catch (error) {
    console.error("Error calling OpenAI:", error);
    throw new Error("Failed to generate content");
  }
};

const ModuleDetailPage = () => {
  const { courseId, sectionId, moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [courseName, setCourseName] = useState("");
  const [isQuizMode, setIsQuizMode] = useState(false);
  const [AIChatEnabled, setAIChatEnabled] = useState(false);
  const materialRefs = useRef([]);

  const [lastEditDate, setLastEditDate] = useState(
    module?.lastEditDate ? new Date(module.lastEditDate) : null
  );
  const [activeModal, setActiveModal] = useState(null); // null, "media", "questions"
  const [visibleControls, setVisibleControls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle"); // 'idle', 'saving', or 'saved'
  const [reRecordingIndex, setReRecordingIndex] = useState(null);
  const [selectedContainerId, setSelectedContainerId] = useState(null);
  const [modalPosition, setModalPosition] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showGenerateContent, setShowGenerateContent] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [editingTip, setEditingTip] = useState(null);

  const navigate = useNavigate();
  const { role, uid } = useContext(UserContext);
  const {
    setUnsavedChanges,
    handleProtectedNavigation: globalHandleProtectedNavigation,
  } = useUnsavedChanges();

  // Check if user has access to AI features
  const hasAIAccess = ["admin", "technicianplus", "teacherplus"].includes(role);

  // Training system
  const { showTipForComponent } = useTrainingTips("module_editor");
  const saveButton = useTrainingButton("module_editor", "module_save", "top");
  const generateButton = useTrainingButton(
    "module_editor",
    "module_generate",
    "top"
  );
  const settingsButton = useTrainingButton(
    "module_editor",
    "module_settings",
    "top"
  );
  const contentButtons = useTrainingSection(
    "module_editor",
    "module_add_content",
    "top"
  );
  const contentButtonsEmpty = useTrainingSection(
    "module_editor",
    "module_add_content",
    "bottom"
  );

  // Flip handler function that can be passed to question components
  const handleFlip = (materialIndex) => (e) => {
    e.stopPropagation();

    const element = materialRefs.current[materialIndex];
    if (element && !element.classList.contains("is-flipping")) {
      element.classList.add("is-flipping");

      setFlippedCards((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(materialIndex)) {
          newSet.delete(materialIndex);
        } else {
          newSet.add(materialIndex);
        }
        return newSet;
      });

      setTimeout(() => {
        element.classList.remove("is-flipping");
      }, 1200); // Match CSS transition duration
    }
  };

  // TipSide component for back of cards - shows/edits teacher's tip
  const TipSide = ({ material, materialIndex }) => {
    const handleTipClick = (e) => {
      e.stopPropagation();
      setEditingTip(materialIndex);
    };

    const handleTipSave = (_, content) => {
      setMaterials((prevMaterials) => {
        const newMaterials = [...prevMaterials];
        newMaterials[materialIndex] = {
          ...newMaterials[materialIndex],
          tip: content,
        };
        return newMaterials;
      });
      setEditingTip(null);
    };

    const handleCloseModal = () => {
      setEditingTip(null);
    };

    return (
      <div className="card-face back">
        <div className={`question-container ${material.border || "shadow"}`}>
          <div
            className={`question-header tip-back-header ${
              editingTip === materialIndex ? "modal-open" : ""
            }`}
            onClick={
              editingTip === materialIndex
                ? (e) => e.stopPropagation()
                : undefined
            }
            onMouseDown={
              editingTip === materialIndex
                ? (e) => e.stopPropagation()
                : undefined
            }
            onKeyDown={
              editingTip === materialIndex
                ? (e) => e.stopPropagation()
                : undefined
            }
          >
            {editingTip === materialIndex ? (
              <AddTextModal
                show={true}
                onClose={handleCloseModal}
                onSave={handleTipSave}
                initialContent={material.tip || ""}
                index={materialIndex}
                isNewMaterial={!material.tip}
              />
            ) : (
              <div
                onClick={handleTipClick}
                style={{ width: "100%", height: "100%" }}
              >
                {material.tip ? (
                  <>
                    <h3 className="tip-title">Teacher's Tip</h3>
                    <div
                      className="tip-text"
                      dangerouslySetInnerHTML={{ __html: material.tip }}
                    />
                  </>
                ) : (
                  <div className="tip-side-placeholder">
                    Click here to add a teacher's tip for this question
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="tip-return-button-container">
            <button
              className="tip-return-button"
              onClick={handleFlip(materialIndex)}
            >
              <FontAwesomeIcon icon={faRetweet} size="lg" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const toggleControls = (index) => {
    setVisibleControls((prevVisibleControls) =>
      prevVisibleControls.includes(index)
        ? prevVisibleControls.filter((i) => i !== index)
        : [...prevVisibleControls, index]
    );
  };

  useEffect(() => {
    const fetchModule = async () => {
      setIsLoading(true);
      try {
        const result = await fetchModuleData({ courseId, sectionId, moduleId });
        const moduleData = result.data.moduleData;
        setModule(moduleData);
        setIsQuizMode(moduleData.isQuizMode || false);
        setAIChatEnabled(moduleData.AIChatEnabled || false);

        const materialsWithScores = (moduleData.materials || []).map(
          (material) => ({
            ...material,
            score:
              material.score ??
              (material.type === "questions" ||
              material.type === "audio-recorder"
                ? 1
                : 0),
            questionCategories: material.questionCategories || [],
          })
        );

        setMaterials(materialsWithScores);
      } catch (error) {
        console.error("Error fetching module:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCourseDetails = async () => {
      try {
        const result = await getCourses();
        const course = result.data.courses.find(
          (course) => course.id === courseId
        );
        if (course) {
          setCourseName(course.courseName);
          setLastEditDate(
            course.lastEditDate ? new Date(course.lastEditDate) : null
          );
        }
      } catch (error) {
        console.error("Error fetching course details:", error);
      }
    };

    fetchModule();
    fetchCourseDetails();
  }, [courseId, sectionId, moduleId]);

  // Prevent navigation when there are unsaved changes
  useEffect(() => {
    const checkUnsavedChanges = () => {
      return (
        materials.some((material) => material.isNew) || saveStatus === "saving"
      );
    };

    const handleBeforeUnload = (e) => {
      if (checkUnsavedChanges()) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes that will be lost. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    // Add beforeunload listener for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [materials, saveStatus]);

  useEffect(() => {
    let apiLoaded = false;

    const loadYouTubeAPI = () => {
      if (
        !apiLoaded &&
        !document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]'
        )
      ) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        apiLoaded = true;
      }
    };

    loadYouTubeAPI();
  }, []);

  useEffect(() => {
    const initializePlayers = () => {
      materials.forEach((material, index) => {
        if (material.type === "video" && material.youtubeLink) {
          new YT.Player(`youtube-player-${index}`, {
            height: "360",
            width: "640",
            videoId: new URL(material.youtubeLink).searchParams.get("v"),
          });
        }
      });
    };

    if (typeof YT !== "undefined" && YT.Player) {
      initializePlayers();
    } else {
      window.onYouTubeIframeAPIReady = initializePlayers;
    }
  }, [materials]);

  const handleAddMaterial = (material) => {
    setMaterials((prevMaterials) => {
      const newMaterials = [...prevMaterials];
      if (selectedContainerId !== null) {
        newMaterials.splice(selectedContainerId + 1, 0, material);
        return newMaterials;
      }
      return [...newMaterials, material];
    });
  };

  const handleAddImage = async (data) => {
    const { image, caption } = data;
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `courses/${courseId}/sections/${sectionId}/modules/${moduleId}/images/${Date.now()}_${
        image.name
      }`
    );

    try {
      await uploadBytes(storageRef, image);
      const downloadURL = await getDownloadURL(storageRef);

      const newMaterial = createNewMaterial("image", {
        imageURL: downloadURL,
        caption: caption,
      });
      handleAddMaterial(newMaterial);
    } catch (error) {
      console.error("Error uploading image: ", error);
    }
  };

  const handleAddVideo = async (data) => {
    const { video, youtubeLink, caption } = data;
    const newMaterial = createNewMaterial("video", {
      caption: caption,
    });

    if (video) {
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `courses/${courseId}/sections/${sectionId}/modules/${moduleId}/videos/${Date.now()}_${
          video.name
        }`
      );

      try {
        await uploadBytes(storageRef, video);
        const downloadURL = await getDownloadURL(storageRef);
        newMaterial.videoURL = downloadURL;
      } catch (error) {
        console.error("Error uploading video: ", error);
        return;
      }
    } else if (youtubeLink) {
      newMaterial.youtubeLink = youtubeLink;
    }

    handleAddMaterial(newMaterial);
  };

  const handleAddAudio = async (audioBlob, index, questionId) => {
    const storage = getStorage();
    const storageRef = ref(
      storage,
      `courses/${courseId}/sections/${sectionId}/modules/${moduleId}/audios/${questionId}_${Date.now()}.wav`
    );

    try {
      await uploadBytes(storageRef, audioBlob);
      const downloadURL = await getDownloadURL(storageRef);

      setMaterials((prevMaterials) => {
        const newMaterials = [...prevMaterials];
        // Update the existing material at the specified index
        newMaterials[index] = {
          ...newMaterials[index],
          type: "audio",
          audioURL: downloadURL,
          questionId: questionId,
          size: "original",
        };
        return newMaterials;
      });

      setReRecordingIndex(null);
    } catch (error) {
      console.error("Error uploading audio: ", error.message);
      alert(`Failed to upload audio. Error: ${error.message}`);
    }
  };

  const handleAddText = (e) => {
    e.stopPropagation();

    const buttonRect = e.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY;
    const modalPosition = {
      top: buttonRect.top + scrollY - 150,
    };

    setModalPosition(modalPosition);
    setActiveModal(null);

    const newIndex =
      selectedContainerId !== null ? selectedContainerId + 1 : materials.length;

    const newMaterial = createNewMaterial("text", {
      content: "",
      alignment: "left",
      border: "",
      isNew: true,
    });

    handleAddMaterial(newMaterial);
  };

  const handleAddQuestions = (questionData) => {
    const newMaterial = {
      ...createQuestionMaterial(questionData.questionType),
      isNew: true,
    };

    setMaterials((prevMaterials) => {
      const newMaterials = [...prevMaterials];
      // If we have a selected container, insert after it
      if (selectedContainerId !== null) {
        newMaterials.splice(selectedContainerId + 1, 0, newMaterial);
        return newMaterials;
      }
      // Otherwise append to end
      return [...newMaterials, newMaterial];
    });

    setActiveModal(null);
  };

  const handleSizeChange = (index, newSize) => {
    const newMaterials = materials.map((material, i) =>
      i === index ? { ...material, size: newSize } : material
    );
    setMaterials(newMaterials);
  };

  const handleBorderChange = (index, newBorder) => {
    const newMaterials = materials.map((material, i) =>
      i === index ? { ...material, border: newBorder } : material
    );
    setMaterials(newMaterials);
  };

  const handleSave = async () => {
    try {
      setSaveStatus("saving");

      // First, categorize materials that don't have categories yet
      const categorizationsPromises = materials
        .filter(
          (m) =>
            (m.type === "questions" || m.type === "audio-recorder") &&
            !m.questionCategories
        )
        .map(async (material) => {
          const categories = await categorizeMaterial(material, showCategories);
          return { id: material.questionId, categories };
        });

      const categorizations = await Promise.all(categorizationsPromises);

      // Update materials with new categories
      const materialsWithCategories = materials.map((material) => {
        const categorization = categorizations.find(
          (c) => c.id === material.questionId
        );
        if (categorization) {
          return {
            ...material,
            questionCategories: categorization.categories,
          };
        }
        return material;
      });

      // Continue with existing save logic using materialsWithCategories
      const validMaterials = materialsWithCategories.filter((material) => {
        if (
          material.type === "questions" ||
          material.type === "audio-recorder" ||
          material.type === "games"
        ) {
          // Ensure score is included and is a number
          material.score = material.score || 1; // Default to 1 if not set
          return true;
        }
        if (material.type === "questions") {
          switch (material.questionType) {
            case "Multiple Choice":
            case "Multiple Checkbox":
              return (
                material.question &&
                material.question.trim() !== "" &&
                material.options &&
                material.options.some((option) => option.trim() !== "")
              );
            case "Fill in the Blanks":
              return (
                material.question &&
                material.question.trim() !== "" &&
                material.correctAnswers &&
                material.correctAnswers.length > 0
              );
            case "Spell the Word":
              return material.sentence && material.sentence.trim() !== "";
            default:
              return true;
          }
        }
        return true;
      });

      const newMaterials = validMaterials.filter((material) => material.isNew);
      const existingMaterials = validMaterials.filter(
        (material) => !material.isNew
      );

      // Save to database using cloud functions
      if (newMaterials.length > 0) {
        await Promise.all(
          newMaterials.map((material) =>
            addMaterial({
              courseId,
              sectionId,
              moduleId,
              material: { ...material, isNew: undefined },
            })
          )
        );
      }

      await updateModuleMaterials({
        courseId,
        sectionId,
        moduleId,
        newMaterials: validMaterials.map(({ isNew, ...material }) => material),
      });

      setMaterials(validMaterials.map(({ isNew, ...material }) => material));
      setLastEditDate(new Date());
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Error saving module content: ", error);
      alert(`Failed to save module content. Error: ${error.message}`);
      setSaveStatus("idle");
    }
  };

  // Update global unsaved changes status
  useEffect(() => {
    const hasChanges =
      materials.some((material) => material.isNew) || saveStatus === "saving";
    setUnsavedChanges(hasChanges);

    // Cleanup when component unmounts
    return () => {
      setUnsavedChanges(false);
    };
  }, [materials, saveStatus, setUnsavedChanges]);

  const moveMaterial = (index, direction) => {
    const newMaterials = [...materials];
    const [movedMaterial] = newMaterials.splice(index, 1);
    const newIndex = index + direction;
    newMaterials.splice(newIndex, 0, movedMaterial);
    setMaterials(newMaterials);

    // Update visibleControls
    setVisibleControls((prevVisibleControls) => {
      const newVisibleControls = prevVisibleControls.map((controlIndex) => {
        if (controlIndex === index) {
          return newIndex;
        } else if (controlIndex === newIndex) {
          return index;
        }
        return controlIndex;
      });
      return newVisibleControls;
    });
  };

  const deleteMaterial = (index) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this material? This action cannot be undone."
    );

    if (!isConfirmed) {
      return; // User cancelled, don't delete
    }

    // First, apply a fixed height to the container to prevent layout shift
    const materialsContainer = document.querySelector(".materials-content");
    const currentHeight = materialsContainer.offsetHeight;
    materialsContainer.style.minHeight = `${currentHeight}px`;

    // Delete the material
    const newMaterials = materials.filter((_, i) => i !== index);
    setMaterials(newMaterials);

    // Release the fixed height after a short delay
    setTimeout(() => {
      materialsContainer.style.minHeight = "";
    }, 50);
  };

  const handleCourseClick = () => {
    globalHandleProtectedNavigation(() => navigate(`/courses/${courseId}`));
  };

  const handleRetry = (index) => {
    setReRecordingIndex(index);
  };

  // Memoize the handleMaterialChange function to prevent recreation on every render
  const handleMaterialChange = useCallback((index, changes) => {
    setMaterials((prevMaterials) => {
      const newMaterials = [...prevMaterials];
      const material = { ...newMaterials[index] };

      // Handle category changes
      if (changes.materialCategories) {
        const questionId = material.questionId;
        material.questionCategories = changes.materialCategories[questionId];
      } else {
        // Handle other changes
        Object.assign(material, changes);
      }

      newMaterials[index] = material;
      return newMaterials;
    });
  }, []);

  // Memoize the handleQuestionChange function
  const memoizedHandleQuestionChange = useMemo(
    () => handleQuestionChange(materials, setMaterials),
    [materials]
  );

  const getQuestionNumber = (currentIndex) => {
    let questionCount = 0;
    for (let i = 0; i <= currentIndex; i++) {
      if (isCountableQuestion(materials[i])) {
        questionCount++;
      }
    }
    return questionCount;
  };

  const handleContainerClick = (index, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setModalPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setSelectedContainerId(index);
  };

  const shouldShowButtonsAfter = (index) => {
    if (selectedContainerId !== null) {
      return selectedContainerId === index;
    }
    return index === materials.length - 1;
  };

  const handleButtonClick = (e, modalType) => {
    e.stopPropagation();

    // Get the button's position relative to the viewport
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY;

    // Calculate the modal position to be just above the button
    const modalPosition = {
      top: buttonRect.top + scrollY - 150, // 150px above the button
    };

    setActiveModal(modalType);
    setModalPosition(modalPosition);
  };

  const handleToggleCategories = () => {
    setShowCategories(!showCategories);
  };

  const handleGenerateContent = async (
    description,
    questionCounts,
    materials = null
  ) => {
    // If materials are provided directly (from manual generation), add them
    if (materials) {
      setMaterials((prev) => [...prev, ...materials]);
      return;
    }

    // Original AI generation logic
    if (!hasAIAccess) {
      alert(
        "Please upgrade your account. You need a TeacherPlus account to use AI features."
      );
      return;
    }

    try {
      const prompt = generatePrompt(description, questionCounts);

      // Call your AI service here (e.g., OpenAI API)
      const aiResponse = await callAIService(prompt);

      // Process the response
      const newMaterials = processAIResponse(aiResponse);

      // Add the new materials to the existing ones
      setMaterials((prev) => [...prev, ...newMaterials]);
    } catch (error) {
      console.error("Error generating content:", error);
      // Handle error (show notification, etc.)
    }
  };

  const handleTextChange = (index, content) => {
    setMaterials((prevMaterials) => {
      const newMaterials = [...prevMaterials];
      newMaterials[index] = {
        ...newMaterials[index],
        content,
        isNew: false, // Remove the isNew flag once content has been edited
      };
      return newMaterials;
    });
    setIsEditing(null);
  };

  const handleModeToggle = async () => {
    try {
      const updateModuleModeFunction = httpsCallable(
        functions,
        "updateModuleMode"
      );
      const result = await updateModuleModeFunction({
        courseId,
        sectionId,
        moduleId,
        setting: "isQuizMode",
        value: !isQuizMode,
      });

      const newSettings = result.data.settings;
      setIsQuizMode(newSettings.isQuizMode);
      setAIChatEnabled(newSettings.AIChatEnabled);
      setModule((prev) => ({
        ...prev,
        isQuizMode: newSettings.isQuizMode,
        AIChatEnabled: newSettings.AIChatEnabled,
      }));
    } catch (error) {
      console.error("Error updating module mode:", error);
      alert("Failed to update module mode. Please try again.");
    }
  };

  const handleAIChatToggle = async () => {
    try {
      const updateModuleModeFunction = httpsCallable(
        functions,
        "updateModuleMode"
      );
      const result = await updateModuleModeFunction({
        courseId,
        sectionId,
        moduleId,
        setting: "AIChatEnabled",
        value: !AIChatEnabled,
      });

      const newSettings = result.data.settings;
      setAIChatEnabled(newSettings.AIChatEnabled);
      setModule((prev) => ({
        ...prev,
        AIChatEnabled: newSettings.AIChatEnabled,
      }));
    } catch (error) {
      console.error("Error updating AI chat setting:", error);
      alert(
        error.message || "Failed to update AI chat setting. Please try again."
      );
    }
  };

  const handleViewAsStudent = () => {
    globalHandleProtectedNavigation(() =>
      navigate(
        `/sview/courses/${courseId}/sections/${sectionId}/modules/${moduleId}/student/${uid}`
      )
    );
  };

  if (!module || isLoading) {
    return (
      <div className="loading-message">Module contents are loading...</div>
    );
  }

  return (
    <div className="module-detail-page">
      <div className="status-bar">
        <span
          className="status-field clickable-course-name"
          onClick={handleCourseClick}
        >
          Course: {courseName}
        </span>
        <span className="status-field">Module: {module.name}</span>
        <span className="status-field">
          Edited: {lastEditDate ? lastEditDate.toLocaleString() : "N/A"}
        </span>
        <span
          className="status-field clickable-course-name"
          onClick={handleViewAsStudent}
          style={{ cursor: "pointer" }}
        >
          <FontAwesomeIcon
            icon={faEye}
            className="button-icon"
            style={{ marginRight: "4px" }}
          />
          View as Student
        </span>
      </div>
      <div className="materials">
        <div className="materials-content">
          {materials.length === 0 && (
            <div
              ref={contentButtonsEmpty.ref}
              className={`content-buttons-container ${contentButtonsEmpty.className}`}
              onMouseEnter={contentButtonsEmpty.onMouseEnter}
            >
              <div className="content-buttons">
                <button
                  className="content-button"
                  onClick={(e) => handleButtonClick(e, "media")}
                >
                  <FontAwesomeIcon icon={faImage} className="button-icon" />
                  <span className="button-text-add">Add</span> Media
                </button>
                <button
                  className="content-button"
                  onClick={(e) => handleAddText(e)}
                >
                  <FontAwesomeIcon icon={faFont} className="button-icon" />
                  <span className="button-text-add">Add</span> Text
                </button>
                <button
                  className="content-button"
                  onClick={(e) => handleButtonClick(e, "questions")}
                >
                  <FontAwesomeIcon icon={faQuestion} className="button-icon" />
                  <span className="button-text-add">Add</span> Questions
                </button>
              </div>
            </div>
          )}

          {materials.map((material, index) => (
            <React.Fragment key={`${material.id || index}-${materials.length}`}>
              <div
                ref={(el) => (materialRefs.current[index] = el)}
                className={`material-item ${
                  material.type === "video" && material.youtubeLink
                    ? "original"
                    : material.size
                } ${material.border} ${material.type} ${
                  flippedCards.has(index) ? "flipped" : ""
                }`}
                onClick={(e) => handleContainerClick(index, e)}
              >
                {!flippedCards.has(index) && (
                  <>
                    <div
                      className="material-controls-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleControls(index);
                      }}
                    >
                      <FaEllipsisH />
                    </div>
                    <div
                      className={`material-controls ${
                        visibleControls.includes(index) ? "visible" : ""
                      }`}
                    >
                      <div className="material-controller-menus">
                        {!(material.type === "video" && material.youtubeLink) &&
                          material.type !== "questions" &&
                          material.type !== "audio-recorder" &&
                          material.type !== "audio" && (
                            <select
                              value={material.size}
                              onChange={(e) =>
                                handleSizeChange(index, e.target.value)
                              }
                            >
                              <option value="very-small">Very Small</option>
                              <option value="small">Small</option>
                              <option value="medium">Medium</option>
                              <option value="large">Large</option>
                              <option value="very-large">Very Large</option>
                              <option value="original">Original Size</option>
                            </select>
                          )}
                        <select
                          value={material.border}
                          onChange={(e) =>
                            handleBorderChange(index, e.target.value)
                          }
                        >
                          <option value="shadow">Shadow</option>
                          <option value="black-border">Black Border</option>
                          <option value="red-border">Red Border</option>
                          <option value="no-border">No Border</option>
                        </select>
                      </div>
                      <div className="material-controller-buttons">
                        <button
                          className="move-material-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveMaterial(index, -1);
                          }}
                          disabled={index === 0}
                        >
                          ▲
                        </button>
                        <button
                          className="move-material-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveMaterial(index, 1);
                          }}
                          disabled={index === materials.length - 1}
                        >
                          ▼
                        </button>
                        <button
                          className="delete-material-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMaterial(index);
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Front face of the card */}
                <div className="card-face front">
                  {isCountableQuestion(material) && (
                    <span
                      className={`question-number ${
                        getQuestionNumber(index) >= 10 ? "double-digit" : ""
                      }`}
                    >
                      {getQuestionNumber(index)}
                    </span>
                  )}
                  {material.type === "image" && (
                    <>
                      <img src={material.imageURL} alt="Uploaded" />
                      {material.caption && <p>{material.caption}</p>}
                    </>
                  )}
                  {material.type === "video" && (
                    <>
                      {material.videoURL ? (
                        <video controls>
                          <source src={material.videoURL} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : material.youtubeLink ? (
                        <div className="iframe-wrapper youtube-wrapper">
                          <div id={`youtube-player-${index}`}></div>
                        </div>
                      ) : (
                        <p>No video source available</p>
                      )}
                      {material.caption && <p>{material.caption}</p>}
                    </>
                  )}
                  {material.type === "audio" && (
                    <div>
                      {reRecordingIndex === index ? (
                        <AudioRecorder
                          onSave={(audioBlob) =>
                            handleAddAudio(
                              audioBlob,
                              index,
                              material.questionId
                            )
                          }
                          onRecordingComplete={() => {}}
                          questionId={material.questionId}
                          question={material.question}
                          onChange={(changes) =>
                            handleMaterialChange(index, changes)
                          }
                          isStudentView={false}
                          border={material.border}
                          score={material.score}
                          onFlip={handleFlip(index)}
                        />
                      ) : (
                        <AudioPlayer
                          audioURL={material.audioURL}
                          onRetry={() => handleRetry(index)}
                          question={material.question}
                          onChange={(changes) =>
                            handleMaterialChange(index, changes)
                          }
                          isStudentView={false}
                          border={material.border}
                          score={material.score}
                          tip={material.tip}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.caption && <p>{material.caption}</p>}
                    </div>
                  )}
                  {material.type === "text" && (
                    <div
                      className={`question-container ${
                        material.border || "shadow"
                      }`}
                    >
                      <div className="question-header">
                        {isEditing === index || !material.content ? (
                          <AddTextModal
                            show={true}
                            onClose={(shouldDelete) => {
                              if (shouldDelete) {
                                deleteMaterial(index);
                              }
                              setIsEditing(null);
                            }}
                            onSave={(idx, content) =>
                              handleTextChange(idx, content)
                            }
                            initialContent={material.content}
                            index={index}
                            isNewMaterial={material.isNew}
                          />
                        ) : (
                          <div
                            className="text-content"
                            dangerouslySetInnerHTML={{
                              __html: material.content,
                            }}
                            onClick={() => setIsEditing(index)}
                          />
                        )}
                      </div>
                      <div className="question-footer">
                        <div className="question-footer-flex">
                          <div className="button-wrapper"></div>
                          <TipButton
                            tip={material.tip}
                            onChange={(changes) =>
                              handleMaterialChange(index, changes)
                            }
                            isStudentView={false}
                            onFlip={handleFlip(index)}
                          />
                          <div className="scorebox-container"></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {material.type === "questions" && (
                    <>
                      {material.questionType === "Multiple Choice" && (
                        <MultipleChoiceQuestion
                          question={material.question}
                          options={material.options}
                          correctAnswers={material.correctAnswers || []}
                          score={material.score}
                          border={material.border}
                          tip={material.tip}
                          questionCategories={material.questionCategories || []}
                          questionId={material.questionId}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "Multiple Checkbox" && (
                        <MultipleCheckboxQuestion
                          question={material.question}
                          options={material.options}
                          correctAnswers={material.correctAnswers || []}
                          score={material.score}
                          border={material.border}
                          tip={material.tip}
                          questionCategories={material.questionCategories || []}
                          questionId={material.questionId}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "Fill in the Blanks" && (
                        <FillInTheBlanks
                          question={material.question}
                          correctAnswers={material.correctAnswers || []}
                          score={material.score}
                          border={material.border}
                          tip={material.tip}
                          questionCategories={material.questionCategories || []}
                          questionId={material.questionId}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "Spell the Word" && (
                        <SpellTheWord
                          sentence={material.sentence}
                          score={material.score}
                          border={material.border}
                          tip={material.tip}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "Sentence Ordering" && (
                        <SentenceOrdering
                          sentences={material.sentences || []}
                          correctAnswers={material.correctAnswers || []}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          border={material.border}
                          score={material.score}
                          tip={material.tip}
                          questionCategories={material.questionCategories || []}
                          questionId={material.questionId}
                          question={material.question || ""}
                          isStudentView={false}
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "Pairing" && (
                        <Pairing
                          question={material.question}
                          leftItems={material.leftItems}
                          rightItems={material.rightItems}
                          pairs={material.pairs}
                          score={material.score}
                          border={material.border}
                          tip={material.tip}
                          questionCategories={material.questionCategories || []}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          isStudentView={false}
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "The Guessinator" && (
                        <TheGuessinator
                          question={material.question}
                          gameWord={material.gameWord}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          isStudentView={false}
                          border={material.border}
                          tip={material.tip}
                          onFlip={handleFlip(index)}
                        />
                      )}
                      {material.questionType === "Flashcards" && (
                        <Flashcards
                          question={material.question}
                          cards={material.cards || []}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          isStudentView={false}
                          border={material.border}
                          tip={material.tip}
                        />
                      )}
                      {material.questionType === "Open-Ended Text" && (
                        <OpenTextQuestion
                          content={material.content}
                          score={material.score}
                          border={material.border}
                          tip={material.tip}
                          questionCategories={material.questionCategories || []}
                          questionId={material.questionId}
                          onChange={(changes) =>
                            memoizedHandleQuestionChange(material, changes)
                          }
                          isStudentView={false}
                          showCategories={showCategories}
                          onFlip={handleFlip(index)}
                        />
                      )}
                    </>
                  )}
                  {material.type === "audio-recorder" && (
                    <AudioRecorder
                      onSave={(audioBlob) =>
                        handleAddAudio(audioBlob, index, material.questionId)
                      }
                      onRecordingComplete={() => {}}
                      questionId={material.questionId}
                      question={material.question}
                      onChange={(changes) =>
                        handleMaterialChange(index, changes)
                      }
                      isStudentView={false}
                      border={material.border || "shadow"}
                      score={material.score}
                      tip={material.tip}
                      onFlip={handleFlip(index)}
                    />
                  )}
                </div>

                {/* Back face of the card - only for cards that aren't flashcards */}
                {!(
                  material.type === "questions" &&
                  material.questionType === "Flashcards"
                ) && <TipSide material={material} materialIndex={index} />}
              </div>

              {shouldShowButtonsAfter(index) && (
                <div
                  ref={contentButtons.ref}
                  className={`content-buttons-container ${contentButtons.className}`}
                  onMouseEnter={contentButtons.onMouseEnter}
                >
                  <div className="content-buttons">
                    <button
                      className="content-button"
                      onClick={(e) => handleButtonClick(e, "media")}
                    >
                      <FontAwesomeIcon icon={faImage} className="button-icon" />
                      <span className="button-text-add">Add</span> Media
                    </button>
                    <button
                      className="content-button"
                      onClick={(e) => handleAddText(e)}
                    >
                      <FontAwesomeIcon icon={faFont} className="button-icon" />
                      <span className="button-text-add">Add</span> Text
                    </button>
                    <button
                      className="content-button"
                      onClick={(e) => handleButtonClick(e, "questions")}
                    >
                      <FontAwesomeIcon
                        icon={faQuestion}
                        className="button-icon"
                      />
                      <span className="button-text-add">Add</span> Questions
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <AddContentModal
        show={!!activeModal}
        modalType={activeModal}
        onClose={() => setActiveModal(null)}
        onSave={(type, data) => {
          if (type === "audio") {
            const newMaterial = {
              type: "audio-recorder",
              questionId: uuidv4(),
              border: "shadow",
              isNew: true,
            };
            handleAddMaterial(newMaterial);
          } else if (type === "image") {
            handleAddImage(data);
          } else if (type === "video") {
            handleAddVideo(data);
          } else {
            // This is for question types
            handleAddQuestions(type);
          }
          setActiveModal(null);
        }}
        position={modalPosition}
      />
      <div className="button-container">
        <div className="button-controls">
          <button
            ref={settingsButton.ref}
            className={`settings-button ${settingsButton.className}`}
            onClick={() => setShowSettings(true)}
            onMouseEnter={settingsButton.onMouseEnter}
          >
            <FontAwesomeIcon
              icon={faCog}
              className="button-icon settings-icon"
            />
            Settings
          </button>
          <button
            ref={generateButton.ref}
            className={`generate-content-button ${generateButton.className}`}
            onClick={() => setShowGenerateContent(true)}
            onMouseEnter={generateButton.onMouseEnter}
            title="Generate content using AI"
          >
            <FontAwesomeIcon
              icon={faMagicWandSparkles}
              className="button-icon generate-icon"
            />
            Generate Content
          </button>
          <button
            ref={saveButton.ref}
            className={`save-button ${saveButton.className}`}
            onClick={handleSave}
            onMouseEnter={saveButton.onMouseEnter}
            disabled={saveStatus !== "idle"}
          >
            <FontAwesomeIcon
              icon={faSave}
              className="button-icon save-icon"
              spin={saveStatus === "saving"}
            />
            {saveStatus === "saving"
              ? "Saving..."
              : saveStatus === "saved"
              ? "Changes Saved!"
              : "Save Changes"}
          </button>
        </div>
      </div>
      <ModuleSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        showCategories={showCategories}
        onToggleCategories={handleToggleCategories}
        hasAIAccess={hasAIAccess}
        isQuizMode={isQuizMode}
        onModeToggle={handleModeToggle}
        AIChatEnabled={AIChatEnabled}
        onAIChatToggle={handleAIChatToggle}
      />
      <GenerateContentModal
        show={showGenerateContent}
        onClose={() => setShowGenerateContent(false)}
        onGenerate={handleGenerateContent}
        hasAIAccess={hasAIAccess}
      />
    </div>
  );
};

export default ModuleDetailPage;
